import { Device } from '@capacitor/device';
import {TranslateService} from '@ngx-translate/core';
import {FaultPatch} from '@docma-mm-core/fault/patch/fault-patch';
import JSZip from 'jszip';
import {Activity} from '@docma-mm-core/activity/activity';
import {Status} from '@docma-mm-core/status/status';

export function isIdsEquals(obj1: any, obj2: any) {
  const id1 = obj1 ? obj1.Id : null;
  const id2 = obj2 ? obj2.Id : null;

  return id1 === id2;
}

export function IsBoolsEqual(b1: boolean, b2: boolean) {
  return (!!b1) === (!!b2);
}

export function IsNumbersEqual(n1: number, n2: number) {
  if (!n1 && !n2) {
    return true;
  }
  return n1 === n2;
}

export function toBoolean(value: string): boolean {
  return ['true', '1', 'yes'].includes(
    value.trim().toLowerCase()
  );
}

export function isDateEqual(d1: Date, d2: Date): boolean {
  if (!d1 && !d2) {
    return true;
  }
  if (d1 && d2 && new Date(d1).getTime() === new Date(d2).getTime()) {
    return true;
  }
  return false;
}

export function sanitizeFileName(fileName: string): string {
  let sanitized = fileName.replace(/[<>:"\/\\|?*\x00-\x1F]/g, '_');
  // Trim spaces and dots from the start and end
  sanitized = sanitized.replace(/^[ .]+|[ .]+$/g, '');
  return sanitized;
}

export function isStringsEqual(s1: string, s2: string): boolean {
  if ((s1 && !s2) || (!s1 && s2) || (s1 && s2 && s1 !== s2)) {
    return false;
  }
  return true;
}

export function getDuplicateName(duplicateName: string, names: string[], translateService: TranslateService): string {
  const namesDict: {[ixd: number]: string} = [];
  const escapeChars = /[\!\#\$\%\^\&\*\)\(\+\=\.\<\>\{\}\[\]\:\;\'\"\|\~\`\_\-]/g;

  const escapedDuplicateName = duplicateName.replace(escapeChars, '\\$&');

  for (const name of names) {
    const matchFirst = name.match(`${escapedDuplicateName}${translateService.instant('MM.general.copy.matchFirst')}`);
    const matchNext = name.match(`${escapedDuplicateName}${translateService.instant('MM.general.copy.matchNext')}`);

    if (matchFirst) {
      namesDict[0] = name;
    }
    else if (matchNext) {
      const subMatch = name.slice(matchNext.index, name.length).match(translateService.instant('MM.general.copy.matchNumber'));
      const i = +name.slice(matchNext.index + subMatch.index + 1, name.length - 1);
      namesDict[i] = name;
    }
    else if (duplicateName == name) {
      namesDict[-1] = name;
    }
  }

  let nameSuffix: string;

  if (Object.keys(namesDict).length === 0) {
    nameSuffix = '';
  }
  else if (Object.keys(namesDict).length === 1) {
    nameSuffix = translateService.instant('MM.general.copy.suffix');
  }
  else {
    nameSuffix = `${translateService.instant('MM.general.copy.suffix')} (${Math.max(...Object.keys(namesDict).map(x => +x)) + 1})`;
  }
  return nameSuffix;
}

export function clearDictionary(dictionary: any) {
  for (const key in dictionary) {
    delete dictionary[key];
  }
}

export function pageBySize<T>(input: T[], size: number = 500): Array<Array<T>> {
  const result: Array<Array<T>> = [];
  for (let i = 0; i < input.length; i += size) {
    result.push(input.slice(i, i + size));
  }
  return result;
}

export function pagePatchesBySize(input: { [key: string]: Array<FaultPatch> }, size: number = 50) {
  const result: Array<{ [key: string]: Array<FaultPatch> }> = [];
  const keys = Object.keys(input);

  for (let i = 0; i < keys.length; i += size) {
    const chunk = keys.slice(i, i + size).reduce((acc, key) => {
      acc[key] = input[key];
      return acc;
    }, {} as { [key: string]: Array<FaultPatch> });

    result.push(chunk);
  }
  return result;
}

export function formatDateToYMD(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function isIosDevice(navigatorRef: Navigator = navigator): boolean {
  const navAny: any = navigatorRef as any;
  const ua = navigatorRef.userAgent || '';
  const platform = navigatorRef.platform || '';
  const maxTouchPoints = typeof navAny?.maxTouchPoints === 'number' ? navAny.maxTouchPoints : 0;
  const vendor = (navAny.vendor || '').toString();
  return (
    /iPad|iPhone|iPod|iPhone OS|iPadOS/i.test(ua) ||
    (platform === 'MacIntel' && maxTouchPoints > 1) ||
    (vendor.includes('Apple') && maxTouchPoints > 0 && /Safari\//.test(ua))
  );
}

export function isIosSafari(navigatorRef: Navigator = navigator): boolean {
  const navAny: any = navigatorRef as any;
  const ua = navigatorRef.userAgent || '';
  const vendor = (navAny.vendor || '').toString();
  const hasSafari = /Safari\/\d+/.test(ua) && /AppleWebKit\//.test(ua);
  const hasVersion = /Version\/\d+/.test(ua);
  const isExcluded = /(CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|UCBrowser|Chrome|Chromium|Edg|OPR)/.test(ua);
  return vendor.includes('Apple') && hasSafari && hasVersion && !isExcluded;
}

export function getSafariMajorVersion(navigatorRef: Navigator = navigator): number | null {
  const ua = navigatorRef.userAgent || '';
  const match = ua.match(/Version\/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

export function getIosOsMajorVersion(navigatorRef: Navigator = navigator): number | null {
  const ua = navigatorRef.userAgent || '';
  const match = ua.match(/(?:CPU iPhone OS|CPU OS|iPhone OS|OS)\s(\d+)[_.]/);
  return match ? parseInt(match[1], 10) : null;
}

export async function getDeviceInfo() {
  return await Device.getInfo();
}

export async function getIosVersion(): Promise<string | null> {
  const info = await getDeviceInfo();
  return info.platform === 'ios' ? info.osVersion ?? null : null;
}

export async function getIosMajorVersionCapacitor(): Promise<number | null> {
  const version = await getIosVersion();
  if (!version) { return null; }
  const major = parseInt(version.split('.')[0], 10);
  return Number.isNaN(major) ? null : major;
}

export async function isIos(): Promise<boolean> {
  const info = await getDeviceInfo();
  return info.platform === 'ios';
}

export function setSuspended<T>(
  ctx: Record<string, any>,
  flagName: string,
  fn: () => T
): T {
  const oldValue = ctx[flagName];
  ctx[flagName] = true;

  try {
    return fn();
  } finally {
    ctx[flagName] = oldValue;
  }
}

export function joinUrl(base: string, ...paths: string[]): string {
  const normalizedBase = base.trim().replace(/\/+$/, '');

  const normalizedPaths = paths
    .filter(Boolean)
    .map(path => path.trim().replace(/^\/+|\/+$/g, ''));

  return [normalizedBase, ...normalizedPaths].join('/');
}

export async function getFileHeader(file: File, length = 4): Promise<Uint8Array> {
  const slice = file.slice(0, length);
  const buffer = await slice.arrayBuffer();
  return new Uint8Array(buffer);
}

export function isZipHeader(header: Uint8Array): boolean {
  return (
    header[0] === 0x50 &&
    header[1] === 0x4b &&
    (
      (header[2] === 0x03 && header[3] === 0x04) ||
      (header[2] === 0x05 && header[3] === 0x06) ||
      (header[2] === 0x07 && header[3] === 0x08)
    )
  );
}

export async function isXlsxFile(file: File): Promise<boolean> {

  const header = await this.getFileHeader(file);

  // Must be ZIP container
  if (!this.isZipHeader(header)) { return false; }

  try {
    const zip = await new JSZip().loadAsync(file);

    const hasWorkbook = Object.keys(zip.files).some(path =>
      path.toLowerCase() === 'xl/workbook.xml'
    );

    return hasWorkbook;

  } catch {
    return false;
  }
}

export async function isZipFile(file: File): Promise<boolean> {
  const header = await getFileHeader(file);

  if (!isZipHeader(header)) { return false; }

// If it is XLSX → not plain ZIP
  const isXlsx = await isXlsxFile(file);
  return !isXlsx;
}

export function isEmptyObject(value: unknown) {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  );
}

export function isLockedByStatus(activities: Activity[], lockedStatus: {[id: string]: Status}) {
  return Object.values(
    activities
      .filter(x => x.data.Status && !x.data.Status.IsNeutral)
      .sort((a, b) => (b.data.Date?.getTime() ?? 0) - (a.data.Date?.getTime() ?? 0) || (b.data.Id ?? 0) - (a.data.Id ?? 0))
      .reduce((groups, activity) => {
        const key = activity.data.Status.ViewPoint;
        if (!(key in groups)) {
          groups[key] = activity; // first (latest) activity for this ViewPoint
        }
        return groups;
      }, {} as Record<number, typeof activities[number]>)
  ).some(activity => lockedStatus.hasOwnProperty(activity.data.Status.Id));
}

